/**
 * DashboardPage — entrypoint do dashboard.
 *
 * O backend resolve o role do utilizador autenticado e devolve o payload
 * já formatado para esse role (admin / comercial / engenharia / financeiro
 * / logistica). Aqui limitamo-nos a:
 *
 *   1. Manter o estado do período seleccionado (default ``last_30d`` —
 *      último mês). O `PeriodPicker` permite também escolher um intervalo
 *      personalizado (objecto ``{ id: 'custom', from, to }``).
 *   2. Encaminhar para o sub-componente certo conforme ``data.role``.
 *
 * O wrapper `DashboardShell` injecta o cabeçalho com saudação + filtro de
 * período (vê `components/dashboard`).
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { dashboardAPI } from '@/services/api';
import { Skeleton } from '@/components/ui/skeleton';
import { DashboardShell } from '@/components/dashboard';
import { t } from '@/i18n/translations';

import AdminDashboard from './dashboard/AdminDashboard';
import CommercialDashboard from './dashboard/CommercialDashboard';
import EngineeringDashboard from './dashboard/EngineeringDashboard';
import FinanceDashboard from './dashboard/FinanceDashboard';
import LogisticsDashboard from './dashboard/LogisticsDashboard';

const DEFAULT_PERIOD = 'last_30d';

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <Skeleton className="h-3 w-24" />
          <Skeleton className="mt-2 h-8 w-56" />
        </div>
        <Skeleton className="h-10 w-80" />
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Skeleton className="h-72" />
        <Skeleton className="h-72" />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
      </div>
    </div>
  );
}

const ROLE_RENDERERS = {
  admin: AdminDashboard,
  comercial: CommercialDashboard,
  engenharia: EngineeringDashboard,
  financeiro: FinanceDashboard,
  logistica: LogisticsDashboard,
};

export default function DashboardPage() {
  const { lang, user } = useAuth();
  // Estado do período: string para presets ou ``{ id: 'custom', from, to }``.
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async (p) => {
    setLoading(true);
    setError(false);
    try {
      const res = await dashboardAPI.get(p);
      setData(res.data);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch quando o período muda. ``period`` é state — só muda quando o
  // utilizador clica num preset ou aplica um custom (referência estável
  // entre renders sem ``setPeriod``), portanto pode ir directo nos deps.
  useEffect(() => {
    load(period);
  }, [period, load]);

  if (loading && !data) return <DashboardSkeleton />;
  if (error || !data) {
    return <div className="text-destructive">{t('error_loading_dashboard', lang)}</div>;
  }

  const Renderer = ROLE_RENDERERS[data.role] || CommercialDashboard;

  return (
    <DashboardShell user={user} lang={lang} period={period} onPeriodChange={setPeriod}>
      <Renderer data={data} lang={lang} />
    </DashboardShell>
  );
}
